/**
 * The browser tab's title — one rule for every place that sets or caches it
 * (item 98, Segev 2026-11-04). On a company host such as jewishpro.ai the tab
 * never says the platform's name: not before the brand loads, not on the login
 * page, not after a navigation. Chrome's history keeps whatever title a page had
 * when it was visited, so a single "CorpBrain" moment is recorded for good.
 *
 * The title, first that applies:
 *   1. the brand's own app name (White-Label → App name);
 *   2. on a company host, that host's app name — the name nginx wrote into
 *      the page itself (<meta name="application-name">, from
 *      config/company_hosts.json), so every organization under the company
 *      reads the company's name there;
 *   3. a page title the brand chose itself (White-Label → Page title);
 *   4. the platform's name.
 * The public documents (/about, /privacy, /terms) keep their own titles
 * (modules/legal) on every host, deliberately.
 */

import type { BrandConfig } from './types';
import { defaultBrand } from './default';

/** The platform's own name — the default title, and nginx's default app name. */
export const PLATFORM_TITLE = defaultBrand.pageTitle;

/** The app name this page was served with, when it is a company's (not the platform default). */
export function hostAppName(doc: Pick<Document, 'querySelector'> = document): string | null {
  const name = doc.querySelector<HTMLMetaElement>('meta[name="application-name"]')?.content?.trim();
  return name && name !== PLATFORM_TITLE ? name : null;
}

export function documentTitleFor(brand: Pick<BrandConfig, 'pageTitle' | 'appName'>, host: string | null = hostAppName()): string {
  const own = brand.appName?.trim();
  if (own) return own;
  if (host) return host;
  const chosen = brand.pageTitle?.trim();
  return chosen && chosen !== PLATFORM_TITLE ? chosen : PLATFORM_TITLE;
}
